import { z } from 'zod';

/**
 * ERP LEAVE CODE → HADIR LEAVE TYPE.
 *
 * Its own file rather than another block in `erp.schema.ts`, which is at the
 * file-size ceiling, and because the argument below is long enough to be worth
 * finding.
 *
 * ── WHY A MAPPING AND NOT A MATCH ───────────────────────────────────────────
 * Two places already tried to match these lists automatically and disagreed
 * with each other, which is how "leave is not connected" became a fact nobody
 * questioned:
 *
 *   `reconcile.ts`            compared the ERP's CODE (`LD`) against Hadir's
 *                             CODE (`ANNUAL`) — nothing could ever match, and
 *                             it reported 61 unmatched types every night.
 *   `buildsmart-leave-types`  compared the ERP's NAME against Hadir's NAME, so
 *                             `Annual Leave` matched and `Compassionate leave`
 *                             did not — because Hadir calls it `Bereavement`.
 *
 * Neither is fixable by choosing the better one. `Absence`, `Injury on Duty`
 * and `In Lieu Of Overtime` are not near-misses for anything Hadir carries;
 * they are the customer's own vocabulary, and the two systems are allowed to
 * name the same fact differently. So a person states the answer once.
 *
 * ── WHY THE ERP'S OWN LIST IS NOT ENOUGH ────────────────────────────────────
 * `GetLeaveTypes` returns `leaveCode` and `leaveName` and NOTHING ELSE — no
 * entitlement, no paid/unpaid flag, and, the one that matters most here, no
 * ACTIVE flag. BuildSmart ships 61 leave types and this customer uses 11; the
 * other 50 are `Bargain Council Leave`, `Trade Union Training`, `User Defined
 * 7` — a vendor's superset, switched off on the customer's own Leave Types
 * screen, where the tick is visible and the API is silent about it.
 *
 * A mapping is therefore also the only way to record WHICH ONES ARE REAL. An
 * ERP code nobody maps is assumed to be one of the 50 and is reported quietly;
 * a mapping naming a code the ERP does not return is reported LOUDLY, because
 * that is the failure with no other symptom — the same shape as the `HRA`/`TRA`
 * pay-component codes carried over from another install, which matched nothing
 * and left every salary short without an error anywhere.
 *
 * Empty by default. A base copy knows nothing about anybody's leave policy, and
 * a guessed mapping books somebody's Hajj against their annual entitlement.
 */
/** The clock-card count a leave type adds to, or none. */
export const ErpClockCardBucketSchema = z.enum(['LEAVE', 'SICK', 'ABSENT', 'NONE']);
export type ErpClockCardBucket = z.infer<typeof ErpClockCardBucketSchema>;

export const ErpLeaveTypeMapSchema = z
  .array(
    z.object({
      /** The ERP's own code, e.g. `LD`. Compared case-insensitively. */
      erpCode: z.string().trim().min(1),
      /**
       * `LeaveType.code` in Hadir, e.g. `ANNUAL`.
       *
       * Hadir's code and not its id, so the mapping survives a leave type being
       * deleted and recreated — and so it is legible in a settings blob to
       * somebody who is not holding a database client.
       */
      hadirCode: z.string().trim().min(1),
      /**
       * Which count on the monthly clock-card batch this leave adds to.
       *
       * A `CCBatchLine` carries exactly three: `leaveDays`, `sickDays` and
       * `absentDays` (owner, 2026-10-05: "staff will ask for leaves using
       * Hadir", so the batch has to carry them). Maternity, marriage, Hajj and
       * unpaid leave fit none of them obviously, so a person decides, per
       * type. `NONE` by default for the same reason the list is empty by
       * default: a guess books somebody's Hajj as annual leave.
       */
      clockCard: ErpClockCardBucketSchema.default('NONE'),
    })
  )
  .default([]);

/** Stored mappings written before `clockCard` existed have none: read as NONE. */
export type ErpLeaveTypeMap = Array<Omit<z.infer<typeof ErpLeaveTypeMapSchema>[number], 'clockCard'> & { clockCard?: ErpClockCardBucket }>;
